import React, { type MutableRefObject, type ReactNode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@atlaskit/icon';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { GrabberIcon } from '../grabber-icon/index.tsx';
import { useBacklogDispatch } from '../../../../state/index.tsx';
import { dragEnd, dragStart } from '../../../../state/actions/drag/index.tsx';

interface UseDraggableDividerParams {
	elementRef: MutableRefObject<HTMLDivElement | null>;
	cardListId: string;
}

export function useDraggableDivider({ elementRef, cardListId }: UseDraggableDividerParams): {
	dragPreview: ReactNode;
} {
	const [dragState, setDragState] = useState<
		{ type: 'idle' } | { type: 'dragging'; container: HTMLElement }
	>({ type: 'idle' });
	const dispatch = useBacklogDispatch();

	useEffect(() => {
		if (!elementRef.current) return undefined;

		return draggable({
			element: elementRef.current,
			getInitialData: () => ({
				type: 'DIVIDER',
				cardListId,
			}),
			onDragStart: () => {
				if (fg('relatively_position_cards_on_initial_load')) {
					dispatch(dragStart());
				}
			},
			onDrop: () => {
				if (fg('relatively_position_cards_on_initial_load')) {
					dispatch(dragEnd());
				}
			},
			onGenerateDragPreview: ({ nativeSetDragImage }) => {
				setCustomNativeDragPreview({
					nativeSetDragImage,
					render({ container }) {
						setDragState({ type: 'dragging', container });
						return () => setDragState({ type: 'idle' });
					},
				});
			},
		});
	}, [cardListId, dispatch, elementRef]);

	return {
		dragPreview:
			dragState.type === 'dragging'
				? ReactDOM.createPortal(
						<Icon
							glyph={GrabberIcon}
							size="small"
							primaryColor={token('color.icon.subtle')}
							label=""
						/>,
						dragState.container,
					)
				: null,
	};
}
